import ICAL from 'ical.js'

import type { BrandGroupType, MenuItem, Site, WorkspaceGroup } from '@models/types'
import type { WysiwygElement } from '@models/wysiwyg-types'

import { PUBLICATION_INTERNAL_PAGE_SLUGS } from './constants'
import { type SharedMessages, type SiteLanguageCode, getSharedTranslator, sharedMessages } from './i18n'

export function hasEventEnded(icsString: string | undefined): boolean {
  if (!icsString) return false
  const nextOccurence = getNextOccurrence(icsString)
  if (!nextOccurence) return true
  return new Date(nextOccurence) < new Date()
}

export function getNextOccurrence(icsString: string, now = new Date()): Date | null {
  try {
    const jcalData = ICAL.parse(icsString)
    const vcalendar = new ICAL.Component(jcalData)
    const veventComponent = vcalendar.name === 'vevent' ? vcalendar : vcalendar.getFirstSubcomponent('vevent')
    if (!veventComponent) return null

    const vevent = new ICAL.Event(veventComponent)

    // Check if the event has an UNTIL date in the past
    const rrule = vevent.component.getFirstPropertyValue('rrule') as ICAL.Recur
    if (rrule && rrule.until) {
      const untilDate = rrule.until.toJSDate()
      if (untilDate < now) {
        return null
      }
    }

    if (!vevent.isRecurring()) {
      const start = vevent.startDate.toJSDate()
      return start > now ? start : null
    }

    // Use the iterator method which properly handles all recurrence rules
    const iterator = vevent.iterator()
    let nextDate

    // Find the next occurrence after now
    do {
      nextDate = iterator.next()
      if (!nextDate) {
        break
      }
    } while (nextDate.toJSDate() <= now)

    return nextDate ? nextDate.toJSDate() : null
  } catch (e) {
    console.error('Error finding next occurrence:', e)
    return null
  }
}

export const compareUserAuthorizations = (
  authorizerWorkspaceGroups: WorkspaceGroup[],
  authorizerBrandGroups: BrandGroupType[],
  workspaceGroups: WorkspaceGroup[],
  brandGroups: BrandGroupType[],
): boolean => {
  if (authorizerWorkspaceGroups.includes('Admin')) {
    //admin user has all rights
    return true // after this user is not admin
  }
  // check workspace permissions
  for (const group of workspaceGroups) {
    // check that for each permission given, the user has the right to give it
    if (group === 'Admin' || group === 'Accounting') {
      return false // cannot give admin or Accounting role if you are not admin
    }
    if (group === 'Builder' || group === 'Creator' || group === 'Guest') {
      // Giving a Role on the workspace level is only possible if you are a builder on workspace level
      if (!authorizerWorkspaceGroups.includes('Builder')) {
        return false
      }
    }
  }
  // check brand permissions
  for (const newBrandGroup of brandGroups) {
    const brandGroup = authorizerBrandGroups.find(b => b.brandId === newBrandGroup.brandId)
    for (const group of newBrandGroup.groups) {
      if (group === 'Builder' || group === 'Creator' || group === 'Guest') {
        // trying to give access to a brand
        if (!(
          authorizerWorkspaceGroups.includes('Builder') || // if user is neither builder on workspace level
          (brandGroup && brandGroup.groups.includes('Builder')) // or builder on brand level
        )) {
          return false
        }
      }
    }
  }
  return true
}

export const ERROR_TYPES = {
  WrongField: 'WrongField',
  LeadGeneration: 'LeadGeneration',
  WrongIdPrefix: 'WrongIdPrefix',
  WrongSlugFormat: 'WrongSlugFormat',
  NoToken: 'NoToken',
  NotFound: 'NotFound',
  Unauthorized: 'Unauthorized',
  HeadlineGeneration: 'HeadlineGeneration',
  NoArticle: 'NoArticle',
  BlockNotFound: 'BlockNotFound',
  BlocksNotFound: 'BlocksNotFound',
  PageNotFound: 'PageNotFound',
  BrandNotFound: 'BrandNotFound',
  DomainFormatError: 'DomainFormatError',
  DomainUnavailable: 'DomainUnavailable',
  DomainNotConnected: 'DomainNotConnected',
  NoDomainConnected: 'NoDomainConnected',
  DomainNotFound: 'DomainNotFound',
  NewsLetterNotFound: 'NewsLetterNotFound',
  SlugAlreadyExists: 'SlugAlreadyExists',
  NameAlreadyExists: 'NameAlreadyExists',
  MissingArgument: 'MissingArgument',
  StripeNotConnected: 'StripeNotConnected',
  SubdomainWrongFormat: 'SubdomainWrongFormat',
  WorkspaceNotFound: 'WorkspaceNotFound',
  IdNotAvailable: 'IdNotAvailable',
  LambdaError: 'LambdaError',
  UserNotCreated: 'UserNotCreated',
  BlocksFromMultipleSite: 'BlocksFromMultipleSite',
  UserCreationError: 'UserCreationError',
  DomainAlreadyConnected: 'DomainAlreadyConnected',
  WorkspaceHasActiveBrands: 'WorkspaceHasActiveBrands',
  UserAlreadyExists: 'UserAlreadyExists',
  WrongSiteId: 'WrongSiteId',
  InvalidSiteId: 'InvalidSiteId',
  NewsLetterAlreadyExists: 'NewsLetterAlreadyExists',
  StripeCheckoutError: 'StripeCheckoutError',
  NoSubscriptionsToExport: 'NoSubscriptionsToExport',
  ExportMultipleBrandError: 'ExportMultipleBrandError',
  ExportNonArticlePageError: 'ExportNonArticlePageError',
  CreateSubscriptionError: 'CreateSubscriptionError',
  CancelSubscriptionError: 'CancelSubscriptionError',
  AlreadySubscribed: 'AlreadySubscribed',
  ListSubscriptionError: 'ListSubscriptionError',
  ExportingData: 'ExportingData',
  BadRequest: 'BadRequest',
  StripeDonationProductAlreadyExists: 'StripeDonationProductAlreadyExists',
  SubscriptionPlanInUse: 'SubscriptionPlanInUse',
  SendingDomainNotVerified: 'SendingDomainNotVerified',
  SendTestFailed: 'SendTestFailed',
  FeedParseError: 'FeedParseError',
} as const

export type ERROR_TYPES = keyof typeof ERROR_TYPES

/**
 * Maps a stable API error code to a friendly, localized message.
 * Messages live in the shared i18n catalogs under `errorTypes.*`; codes without
 * a specific message fall back to the generic one (with the code appended for
 * codes that aren't in the generic bucket).
 */
export const getFriendUserErrorMessageFromErrorType = (
  errorType: ERROR_TYPES,
  language?: SiteLanguageCode | null,
): string => {
  const t = getSharedTranslator(language)
  const genericErrorTypes: readonly ERROR_TYPES[] = [
    'BlocksFromMultipleSite',
    'WrongField',
    'LeadGeneration',
    'WrongIdPrefix',
    'NoToken',
    'NotFound',
    'BlockNotFound',
    'BlocksNotFound',
    'PageNotFound',
    'BrandNotFound',
    'MissingArgument',
    'IdNotAvailable',
    'LambdaError',
  ]
  if (genericErrorTypes.includes(errorType)) {
    return t('errorTypes.generic')
  }
  if (errorType in sharedMessages.en.errorTypes) {
    return t(`errorTypes.${errorType as keyof SharedMessages['errorTypes']}`)
  }
  return t('errorTypes.genericWithCode', { errorType })
}

export type MenuItemsOptions =
  'externalUrl' | 'article' | 'section' | 'tag' | 'author' | 'spotlight' | 'internalPage' | 'menu' | 'chatbotOpen'

export const getDropdownChoiceFromMenuItem = (menuItem: MenuItem): MenuItemsOptions | undefined => {
  const { url, type } = menuItem

  if (type === 'MENU') return 'menu'
  if (type === 'CHATBOT_OPEN') return 'chatbotOpen'

  if (!url) return undefined

  if (url.startsWith('/section')) return 'section'

  if (url.startsWith('/tag')) return 'tag'

  if (url.startsWith('/writer')) return 'author'

  if (url.startsWith('/spotlight/')) return 'spotlight'

  if ((Object.values(PUBLICATION_INTERNAL_PAGE_SLUGS) as string[]).includes(url)) return 'internalPage'

  if (url.startsWith('/')) return 'article'

  // There are no validations for external urls
  return 'externalUrl'
}

export const checkHeaderAdUnitIsUnique = ({
  scriptsToCompare: { script1, script2, script3, script4, script5, script6, script7, script8, script9, script10 },
  scriptValue,
}: {
  scriptsToCompare: {
    script1?: string
    script2?: string
    script3?: string
    script4?: string
    script5?: string
    script6?: string
    script7?: string
    script8?: string
    script9?: string
    script10?: string
  }
  scriptValue: string
}): boolean => {
  const { adUnitCode: adUnitScript1 } = getTagInfoFromDocumentHeaderScript(script1 || '')
  const { adUnitCode: adUnitScript2 } = getTagInfoFromDocumentHeaderScript(script2 || '')
  const { adUnitCode: adUnitScript3 } = getTagInfoFromDocumentHeaderScript(script3 || '')
  const { adUnitCode: adUnitScript4 } = getTagInfoFromDocumentHeaderScript(script4 || '')
  const { adUnitCode: adUnitScript5 } = getTagInfoFromDocumentHeaderScript(script5 || '')
  const { adUnitCode: adUnitScript6 } = getTagInfoFromDocumentHeaderScript(script6 || '')
  const { adUnitCode: adUnitScript7 } = getTagInfoFromDocumentHeaderScript(script7 || '')
  const { adUnitCode: adUnitScript8 } = getTagInfoFromDocumentHeaderScript(script8 || '')
  const { adUnitCode: adUnitScript9 } = getTagInfoFromDocumentHeaderScript(script9 || '')
  const { adUnitCode: adUnitScript10 } = getTagInfoFromDocumentHeaderScript(script10 || '')
  const { adUnitCode: adUnitScriptValue } = getTagInfoFromDocumentHeaderScript(scriptValue)

  return (adUnitScript1 && adUnitScript1 === adUnitScriptValue) ||
    (adUnitScript2 && adUnitScript2 === adUnitScriptValue) ||
    (adUnitScript3 && adUnitScript3 === adUnitScriptValue) ||
    (adUnitScript4 && adUnitScript4 === adUnitScriptValue) ||
    (adUnitScript5 && adUnitScript5 === adUnitScriptValue) ||
    (adUnitScript6 && adUnitScript6 === adUnitScriptValue) ||
    (adUnitScript7 && adUnitScript7 === adUnitScriptValue) ||
    (adUnitScript8 && adUnitScript8 === adUnitScriptValue) ||
    (adUnitScript9 && adUnitScript9 === adUnitScriptValue) ||
    (adUnitScript10 && adUnitScript10 === adUnitScriptValue)
    ? false
    : true
}

export const getFontFormatFromFile = (file: string): string => {
  const fontFileType = file.split('.').pop()
  let fontFormat = ''
  switch (fontFileType) {
    case 'woff':
      fontFormat = 'woff'
      break
    case 'woff2':
      fontFormat = 'woff2'
      break
    case 'ttf':
      fontFormat = 'trueType'
      break
    case 'otf':
      fontFormat = 'opentype'
      break
    case 'eot':
      fontFormat = 'embedded-opentype'
      break
    default:
      console.warn('font file extension not recognized', fontFileType, file)
      break
  }
  return fontFormat
}

export const wysiwygElementHasContent = (element: WysiwygElement[] | undefined): boolean => {
  if (!element || !Array.isArray(element)) return false

  // Recursively check if an element (or any of its descendants) contains meaningful content
  const hasContent = (el: WysiwygElement): boolean => {
    // 1) Plain text node
    if ('text' in el) {
      return typeof el.text === 'string' && el.text.trim().length > 0
    }

    // 2) Elements with a specific type that implies content on their own
    if ('type' in el) {
      switch (el.type) {
        case 'custom-code': {
          if (!('html' in el) || typeof el.html !== 'string') return false
          // Strip tags and check for non-whitespace characters
          const textOnly = el.html.replace(/<[^>]*>/g, '').trim()
          return textOnly.length > 0
        }
        case 'iframe': {
          if (!('url' in el) || typeof el.url !== 'string') return false
          return el.url.trim().length > 0
        }
        case 'image-asset':
        case 'image-external': {
          const hrefValue = 'href' in el ? el.href : undefined
          return typeof hrefValue === 'string' && !!hrefValue
        }
        case 'image-local': {
          // Local images are considered content even if metadata fields vary
          return true
        }
        case 'divider':
        case 'break-float': {
          // Visual-only separators shouldn't count as content by themselves
          // Keep searching children (if any) below
          break
        }
        default:
          break
      }
    }

    // 3) Recurse into children for containers like paragraphs, lists, links, tables, etc.
    if ('children' in el) {
      return el.children.some(hasContent)
    }

    return false
  }

  return element.some(hasContent)
}

export type QueryParamsType = keyof typeof queryParams

export const queryParams = {
  article: {
    query_by:
      'title, slug, authors.name, tags.name, section.name, byline, summary, image.caption, image.credit, bodyText, carousel.caption, carousel.credit, metadata',
    query_by_weights: '20, 20, 5, 5, 5, 5, 2, 1, 1, 1, 1, 1, 10',
    exclude_fields: '_vectors, bodyText, bodyMedia',
    highlight_fields: 'none',
    highlight_full_fields: 'none',
  },
  // Same as `article` but keeps `bodyMedia` in responses; used by the CMS assets page (ListAssets)
  articleAssets: {
    query_by:
      'title, slug, authors.name, tags.name, section.name, byline, summary, image.caption, image.credit, bodyText, carousel.caption, carousel.credit, metadata',
    query_by_weights: '20, 20, 5, 5, 5, 5, 2, 1, 1, 1, 1, 1, 10',
    exclude_fields: '_vectors, bodyText',
    highlight_fields: 'none',
    highlight_full_fields: 'none',
  },
  section: {
    query_by: 'name, slug',
    exclude_fields: '_vectors',
    highlight_fields: 'none',
    highlight_full_fields: 'none',
  },
  tag: {
    query_by: 'name, slug',
    exclude_fields: '_vectors',
    highlight_fields: 'none',
    highlight_full_fields: 'none',
  },
  author: {
    query_by: 'name, slug, bio, position, email',
    exclude_fields: '_vectors',
    highlight_fields: 'none',
    highlight_full_fields: 'none',
  },
  spotlight: {
    query_by: 'name, slug',
    exclude_fields: '_vectors',
    highlight_fields: 'none',
    highlight_full_fields: 'none',
  },
  all: {
    query_by:
      'title, slug, name, authors.name, tags.name, section.name, byline, summary, image.caption, image.credit, bodyText, carousel.caption, carousel.credit, metadata',
    query_by_weights: '20, 20, 10, 5, 5, 5, 5, 2, 1, 1, 1, 1, 1, 10',
    exclude_fields: '_vectors, bodyText, bodyMedia',
    highlight_fields: 'none',
    highlight_full_fields: 'none',
  },
}

export const fromUnixToUTC = (timestamp: number | undefined | null): string => {
  // if timestampt is a string and cannot be parsed to a number, return undefined
  if (timestamp === undefined || timestamp === null) {
    return ''
  }
  if (isNaN(timestamp)) {
    console.warn('fromUnixToUTC: timestamp is not a number:', timestamp)
    return ''
  }
  return new Date(timestamp * 1000).toISOString()
}

export const toUnixTimestamp = (date: string | null | undefined): number | undefined | null => {
  if (date === undefined) {
    return undefined
  }
  if (date === null) {
    return null
  }
  const timestamp = new Date(date).getTime()
  // If date is invalid, getTime() returns NaN
  if (isNaN(timestamp)) {
    return null
  }
  return parseInt((timestamp / 1000).toFixed(0))
}

export const getSiteUrl = (site: { domainName?: string | null; domainStatus?: string | null; id: string }): string =>
  `https://${getSiteHost(site)}`

export const getSiteHost = (site: { domainName?: string | null; domainStatus?: string | null; id: string }): string => {
  if (site.domainName && site.domainStatus === 'Active') {
    return site.domainName
  } else {
    return `${site.id}.${process.env.PUBLIC_SITES_DOMAIN}`
  }
}

export const getSiteKey = (site: Site): string => {
  if (site.domainName && site.domainStatus === 'Active') {
    return site.domainName
  } else {
    return site.id
  }
}

export const slugify = (str: string, encloseWithSlash: boolean = true): string => {
  str = str
    .trim()
    .toLowerCase()
    .replace(/[^\w \/-]+/g, '')
    .replace(/ +/g, '-')
    .replace(/\/+/g, '-')

  if (encloseWithSlash) {
    if (!str.startsWith('/')) {
      str = `/${str}`
    }
    if (!str.endsWith('/')) {
      str = `${str}/`
    }
  } else {
    if (str.startsWith('/')) {
      str = str.substr(1)
    }
    if (str.endsWith('/')) {
      str = str.substr(0, str.length - 1)
    }
  }
  return str
}

export const createArticleSlug = ({
  articleTitle,
  sectionName,
  id,
  slugifiedTitle,
}: {
  articleTitle: string
  sectionName?: string | null
  id: string | null
  slugifiedTitle?: string
}): string => {
  const slugifiedSection = slugify(sectionName || 'stories', false)
  const finalSlugifiedTitle = slugifiedTitle ? slugify(slugifiedTitle, false) : slugify(articleTitle, false)
  const slugifiedId = id ? `-${slugify(id, false)}` : ''

  return `/${slugifiedSection}/${finalSlugifiedTitle}${slugifiedId}/`
}

export const createTagSlug = ({ tag }: { tag: string }) => `${PUBLICATION_INTERNAL_PAGE_SLUGS.tags}${slugify(tag)}`

export const createAuthorSlug = ({ author }: { author: string }) =>
  `${PUBLICATION_INTERNAL_PAGE_SLUGS.writers}${slugify(author)}`

export const createSectionSlug = ({ section }: { section: string }) =>
  `${PUBLICATION_INTERNAL_PAGE_SLUGS.sections}${slugify(section)}`

export const createSpotlightSlug = ({ spotlight }: { spotlight: string }) =>
  `${PUBLICATION_INTERNAL_PAGE_SLUGS.spotlight}${slugify(spotlight)}`

export const formatTitleForSeo_Article = (articleTitle: string) =>
  articleTitle.length > 60 ? `${articleTitle.slice(0, 60)}...` : articleTitle

export const formatDescriptionForSeo_Article = (articleSummary: string) =>
  articleSummary.length > 160 ? `${articleSummary.slice(0, 160)}...` : articleSummary

export const formatTitleForSeo_Section = ({
  sectionName,
  siteName,
  language,
}: {
  sectionName: string
  siteName: string
  language?: SiteLanguageCode | null
}) => getSharedTranslator(language)('seo.sectionTitle', { sectionName, siteName })

export const formatDescriptionForSeo_Section = ({
  sectionName,
  siteName,
  language,
}: {
  sectionName: string
  siteName: string
  language?: SiteLanguageCode | null
}) => getSharedTranslator(language)('seo.sectionDescription', { sectionName, siteName })

export const formatTitleForSeo_Tag = ({
  tagName,
  siteName,
  language,
}: {
  tagName: string
  siteName: string
  language?: SiteLanguageCode | null
}) => getSharedTranslator(language)('seo.tagTitle', { tagName, siteName })

export const formatDescriptionForSeo_Tag = ({
  tagName,
  siteName,
  language,
}: {
  tagName: string
  siteName: string
  language?: SiteLanguageCode | null
}) => getSharedTranslator(language)('seo.tagDescription', { tagName, siteName })

export const formatTitleForSeo_Author = ({
  authorName,
  siteName,
  language,
}: {
  authorName: string
  siteName: string
  language?: SiteLanguageCode | null
}) => getSharedTranslator(language)('seo.authorTitle', { authorName, siteName })

export const formatDescriptionForSeo_Author = ({
  authorName,
  siteName,
  language,
}: {
  authorName: string
  siteName: string
  language?: SiteLanguageCode | null
}) => getSharedTranslator(language)('seo.authorDescription', { authorName, siteName })

export const formatImageForSeo_author = (key: string | undefined | null) =>
  key ? `https://${process.env.NEXT_PUBLIC_PUBLIC_ASSET}/${key}` : ''

export const formatTitleForHomepage = ({ siteName, titleTag }: { siteName: string; titleTag?: string }) => {
  return titleTag ? `${siteName} | ${titleTag}` : siteName
}

export const formatDescriptionForHomepage = ({
  siteName,
  description,
  language,
}: {
  siteName: string
  description?: string
  language?: SiteLanguageCode | null
}) => {
  return description
    ? `${description} | ${siteName}`
    : getSharedTranslator(language)('seo.homepageDescription', { siteName })
}

export const serializeSlateToText = (nodes: WysiwygElement[]): string => {
  if (!nodes || !nodes.length || !Array.isArray(nodes)) {
    return ''
  }
  return nodes
    .map(node => {
      if ('text' in node && node.text) {
        return node.text
      }
      if ('children' in node && node.children) {
        return serializeSlateToText(node.children)
      }
      return ''
    })
    .join(' ')
}

type NullToUndefined<T extends Record<string, unknown>> = {
  [K in keyof T]: Exclude<T[K], null>
}

export const makeNullPropertyUndefined = <T extends Record<string, unknown>>(obj: T): NullToUndefined<T> => {
  Object.keys(obj).forEach(key => {
    if (obj[key] === null) {
      delete obj[key]
    }
  })
  return obj as any
}

export const getTagInfoFromDocumentHeaderScript = (
  script: string,
): {
  adUnitCode?: string
  sizes?: [number, number][]
  minWidth?: number
  minHeight?: number
  // maxWidth?: number
  // maxHeight?: number
  allTagInfoIsPresent: boolean
} => {
  // This is an example of the script that we are parsing:
  // <script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"></script>
  // <script>
  //   window.googletag = window.googletag || {cmd: []};
  //   googletag.cmd.push(function() {
  //     googletag.defineSlot('/1094602/arr_app', [[700, 1200], [728, 90], [1200, 700], [800, 500], [700, 700]], 'div-gpt-ad-1713333077152-0').addService(googletag.pubads());
  //     googletag.pubads().enableSingleRequest();
  //     googletag.enableServices();
  //   });
  // </script>
  // The adUnitCode is "/1094602/arr_app". It's the first parameter of the defineSlot function.
  // The sizes are the second parameter of the defineSlot function. The sizes are an array of arrays. Each array has two numbers: the width and the height of the ad.

  // Should return '/1094602/arr_app', [[700, 1200], [728, 90], [1200, 700], [800, 500], [700, 700]], 'div-gpt-ad-1713333077152-0'

  let sizes: [number, number][] | undefined
  let adUnitCode: string | undefined
  let minWidth: number | undefined
  let minHeight: number | undefined
  // let maxWidth: number | undefined
  // let maxHeight: number | undefined
  const defineSlotFunctionMatch = script.match(/googletag\.defineSlot\('([^]+)'/)

  if (defineSlotFunctionMatch !== null) {
    const [arg1, arg2, arg3] = defineSlotFunctionMatch[1].split("'")
    adUnitCode = arg1
    // arg2 will be like: ", [[700, 1200], [728, 90], [1200, 700], [800, 500], [700, 700]], "
    // we need to get an array of numbers from it like: [[700, 1200], [728, 90], [1200, 700], [800, 500], [700, 700]]
    const allArrayOfNumbersInArg2 = arg2.match(/\[(\d+, \d+)\]/g)
    if (allArrayOfNumbersInArg2) {
      const arrayOfNumberForSizes = allArrayOfNumbersInArg2.map(arrayOfNumbers =>
        arrayOfNumbers
          .match(/(\d+), (\d+)/)
          ?.slice(1, 3)
          .map(n => parseInt(n)),
      )
      if (arrayOfNumberForSizes.map(size => size && size[0] && size[1])) {
        sizes = arrayOfNumberForSizes as [number, number][]
        minWidth = sizes.reduce(
          (acc, [width]): number | undefined => {
            if (!acc) return width
            return width < acc ? width : acc
          },
          undefined as undefined | number,
        )
        minHeight = sizes.reduce(
          (acc, [, height]): number | undefined => {
            if (!acc) return height
            return height < acc ? height : acc
          },
          undefined as undefined | number,
        )
        // maxWidth = sizes.reduce(
        //   (acc, [width]): number | undefined => {
        //     if (!acc) return width
        //     return width > acc ? width : acc
        //   },
        //   undefined as undefined | number,
        // )
        // maxHeight = sizes.reduce(
        //   (acc, [, height]): number | undefined => {
        //     if (!acc) return height
        //     return height > acc ? height : acc
        //   },
        //   undefined as undefined | number,
        // )
      }
    }
  }

  return {
    adUnitCode,
    sizes,
    minWidth,
    minHeight,
    // maxWidth,
    // maxHeight,
    allTagInfoIsPresent: !!(adUnitCode && sizes),
  }
}

/**
 *
 * default items for toolbar menu to select different auth-states while mocking
 * https://storybook.js.org/addons/@tomfreudenberg/next-auth-mock?ref=storybookblog.ghost.io
 *
 */
export const mockAuthStates = {
  unknown: {
    title: 'session unknown',
    session: null,
  },
  loading: {
    title: 'session loading',
    session: {
      data: null,
      status: 'loading',
    },
  },
  adminUnAuthed: {
    title: 'admin not authenticated',
    session: {
      data: {
        id: 1,
        login: 'admin',
        role: 'admin',
        roles: ['admin', 'user'],
        username: 'Administrator',
        email: 'admin@local',
      },
      status: 'unauthenticated',
    },
  },
  adminAuthed: {
    title: 'admin authenticated',
    session: {
      data: {
        id: 1,
        login: 'admin',
        role: 'admin',
        roles: ['admin', 'user'],
        username: 'Administrator',
        email: 'admin@local',
      },
      status: 'authenticated',
    },
  },
  userUnAuthed: {
    title: 'user not authenticated',
    session: {
      data: {
        id: 999,
        login: 'user',
        role: 'user',
        roles: ['user'],
        username: 'User',
        email: 'user@local',
      },
      status: 'unauthenticated',
    },
  },
  userAuthed: {
    title: 'user authenticated',
    session: {
      data: {
        id: 999,
        login: 'user',
        role: 'user',
        roles: ['user'],
        username: 'User',
        email: 'user@local',
      },
      status: 'authenticated',
    },
  },
}

export const deduplicateArrayOfObjects = <Type>(arr: Type[], keys: string[]): Type[] =>
  arr.filter(
    (
      s => o =>
        (k => !s.has(k) && s.add(k))(keys.map(k => (o as any)[k]).join('|'))
    )(new Set()),
  )

export const getTypesenseNodeName = (nodeName: string) => {
  const nodes = nodeName.includes('.')
    ? [{ host: nodeName, port: 443, protocol: 'https' }]
    : [
        { host: `${nodeName}-1.a1.typesense.net`, port: 443, protocol: 'https' },
        { host: `${nodeName}-2.a1.typesense.net`, port: 443, protocol: 'https' },
        { host: `${nodeName}-3.a1.typesense.net`, port: 443, protocol: 'https' },
      ]
  const nearestNode = nodeName.includes('.')
    ? undefined
    : {
        host: `${nodeName}.a1.typesense.net`,
        port: 443,
        protocol: 'https',
      }

  return { nodes, nearestNode }
}

const normalizeQueryParams = (queryParams: URLSearchParams, searchKey: string): string | null => {
  if (queryParams.get(searchKey)) {
    return queryParams.get(searchKey)
  }
  // get all the keys that start with the key
  const keys = queryParams.keys()
  const filteredParams: string[] = []
  for (const key of keys) {
    if (key.startsWith(searchKey)) {
      filteredParams.push(queryParams.get(key)!)
    }
  }
  if (filteredParams.length === 0) {
    return null
  }
  return `[${filteredParams.join(',')}]`
}

export type AdditionalFitler = {
  section: string | null
  tag: string | null
  startDate: number | null
  endDate: number | null
  display: string | null
  days: number | null
  author: string | null
  isAPNews: boolean | null
  search: string | null
  state: string | null
  paywall: string | null
  toReview: boolean | null
  sortBy: string | null
  page: string | null
  metadata: string | null
  excludeMetadata: string | null
  excludeSection: string | null
  excludeTag: string | null
  excludeAuthor: string | null
  excludeState: string | null
  excludePaywall: string | null
  feeds: string | null
  articleType: string | null
}

export const generateFilterFromQueryParams = (searchParams: URLSearchParams): AdditionalFitler => {
  const days = searchParams.get('days')
  const startDate = searchParams.get('startDate')
  const endDate = searchParams.get('endDate')
  const apNews = searchParams.get('isAPNews')
  const filter: AdditionalFitler = {
    section: normalizeQueryParams(searchParams, 'section'),
    startDate: startDate ? parseInt(startDate) : null,
    endDate: endDate ? parseInt(endDate) : null,
    display: searchParams.get('display') || null,
    days: days ? parseInt(days) : null,
    tag: normalizeQueryParams(searchParams, 'tag'),
    feeds: normalizeQueryParams(searchParams, 'feeds'),
    articleType: normalizeQueryParams(searchParams, 'articleType'),
    metadata: searchParams.get('metadata') || null,
    excludeMetadata: normalizeQueryParams(searchParams, 'excludeMetadata'),
    excludeSection: normalizeQueryParams(searchParams, 'excludeSection'),
    excludeTag: normalizeQueryParams(searchParams, 'excludeTag'),
    excludeAuthor: normalizeQueryParams(searchParams, 'excludeAuthor'),
    excludeState: normalizeQueryParams(searchParams, 'excludeState'),
    excludePaywall: normalizeQueryParams(searchParams, 'excludePaywall'),
    author: normalizeQueryParams(searchParams, 'author'),
    search: searchParams.get('search') || null,
    state: searchParams.get('state') || null,
    isAPNews: apNews ? (apNews === 'true' ? true : apNews === 'false' ? false : null) : null,
    paywall: searchParams.get('paywall') || null,
    toReview: searchParams.get('toReview')?.toLowerCase() === 'true' || null,
    sortBy: searchParams.get('sortBy') || null,
    page: searchParams.get('page') || null,
  }
  return filter
}

// Normalise an e-mail address so that different casings are treated the same everywhere we store / look it up.
export const normalizeEmail = (email: string | undefined | null): string | undefined =>
  email ? email.toLowerCase().trim() : undefined
